import { useState, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import {
  MarketButton,
  MarketHeader,
  MarketInputSearch,
  MarketField,
  MarketActivityIndicator,
} from '@market/react'
import { MarketInputSearchCustomEvent } from '@market/web-components/loader'
import { ReactComponent as ArrowLeft } from '@market/icons/assets/svg/arrow-left.svg'

import { useAutoFocus } from 'hooks/useAutoFocus'
import ConnectionError from 'components/ConnectionError'
import { loyaltyApi } from 'api'
import { getErrorCode, ErrorCode } from 'utils/api'
import SearchResults from './SearchResults'
import { buildTranslate } from 'locales'
import Switch from 'components/Switch'

const t = buildTranslate('searchCustomers')

const SearchCustomersScreen = () => {
  let history = useHistory()
  const autoFocus = useAutoFocus()

  const firstRender = useRef(true)
  const [searchValue, setSearchValue] = useState('')

  const [lookupUsers, { data, error, status, isFetching }] =
    loyaltyApi().useLazyLookupUsersQuery()
  const searchResults = data?.users ?? []

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
    }
  }, [])

  const onClickSearchButton = async () => {
    const search = (() => {
      if (searchValue.includes('@')) {
        return { email: searchValue }
      }

      return { phone: searchValue }
    })()

    lookupUsers(search)
  }

  const onMarketInputValueChange = (
    event: MarketInputSearchCustomEvent<{
      current: string
    }>
  ) => {
    setSearchValue(event.detail.current)
  }

  const onBackButtonClick = () => {
    history.goBack()
  }

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' && status !== 'pending') {
      ;(document.activeElement as HTMLElement)?.blur()
      onClickSearchButton()
    }
  }

  if (error && getErrorCode(error) !== ErrorCode.NO_USERS_FOUND) {
    return <ConnectionError onClickTryAgain={onClickSearchButton} />
  }

  return (
    <div
      className="market-grid-container"
      data-testid="search-customers-screen"
    >
      <MarketHeader className="market-grid-item-full addons-header" compact>
        <MarketButton
          data-testid="search-customers-back-btn"
          onClick={onBackButtonClick}
          rank="secondary"
          slot="navigation"
        >
          <div slot="icon" className="addons-icon">
            <ArrowLeft className="addons-back-icon" width="24" height="24" />
          </div>
        </MarketButton>
        <h2>{t('title')}</h2>
        <MarketButton
          data-testid="search-customers-submit-btn"
          isLoading={status === 'pending' || undefined}
          onClick={onClickSearchButton}
          rank="primary"
          slot="actions"
        >
          {t('search')}
        </MarketButton>
      </MarketHeader>

      <MarketField
        className="market-grid-item-full"
        data-testid="search-customers-search-bar-field"
        invalid={false}
        name="search-customers-search-bar"
      >
        <MarketInputSearch
          ref={ref => {
            if (firstRender.current) {
              autoFocus(ref)
            }
          }}
          placeholder={t('searchPlaceholder')}
          data-testid="search-customers-search-bar-input"
          className="market-grid-item-full"
          value={searchValue}
          onMarketInputSearchValueChange={onMarketInputValueChange}
        >
          <input
            slot="input"
            onKeyDown={handleKeyPress}
            autoCorrect="off"
            autoCapitalize="none"
          />
        </MarketInputSearch>
        <small
          data-testid="search-customers-search-bar-helper"
          slot="helper"
          className="addons-helper-slot-text"
        >
          {t('instructions')}
        </small>
      </MarketField>

      <Switch
        condition={!isFetching}
        fallback={
          <div className="market-grid-item-full addons-center-items">
            <MarketActivityIndicator data-testid="addons-loading-indicator" />
          </div>
        }
      >
        <Switch
          condition={status !== 'rejected'}
          fallback={
            <div
              className="market-grid-item-full"
              data-testid="search-customers-no-customer-found"
              style={{ textAlign: 'center' }}
            >
              <strong>{t('noResults')}</strong>
              <p>{t('tryAgain')}</p>
            </div>
          }
        >
          <SearchResults users={searchResults} />
        </Switch>
      </Switch>
    </div>
  )
}

export default SearchCustomersScreen
